import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { apiService } from '../../services';
import type { HubSkillsSyncStatus } from '../../types';
import { HubSyncResultsBody } from './HubSyncResultsBody';
import {
  describeCurrentRun,
  formatAbsolute,
  formatRelative,
  formatSchedule,
  runningTooltip,
} from './format';
import './HubSkillsSyncControl.css';

const POLL_MS = 2000;

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export interface HubSkillsSyncControlProps {
  variant?: 'panel' | 'toolbar';
  onSyncSettled?: () => void;
}

export const HubSkillsSyncControl: React.FC<HubSkillsSyncControlProps> = ({
  variant = 'panel',
  onSyncSettled,
}) => {
  const [status, setStatus] = useState<HubSkillsSyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [clicking, setClicking] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const prevRunningRef = useRef(false);
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    const response = await apiService.getHubSkillsSyncStatus();
    if (!response.success) {
      throw new Error(response.message || 'Failed to load Hub sync status');
    }
    setStatus(response.data);
    return response.data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchStatus();
        if (!cancelled) {
          prevRunningRef.current = Boolean(next.sync_in_progress);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load Hub sync status');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [fetchStatus, stopPolling]);

  useEffect(() => {
    const running = Boolean(status?.sync_in_progress);
    if (prevRunningRef.current && !running) {
      setModalOpen(true);
      setNotice(null);
      onSyncSettled?.();
    }
    prevRunningRef.current = running;
  }, [status?.sync_in_progress, onSyncSettled]);

  useEffect(() => {
    if (!status?.sync_in_progress) {
      stopPolling();
      return;
    }
    if (pollRef.current != null) return;
    pollRef.current = window.setInterval(() => {
      fetchStatus().catch(() => {
        /* keep last known status; next tick retries */
      });
    }, POLL_MS);
    return () => stopPolling();
  }, [status?.sync_in_progress, fetchStatus, stopPolling]);

  const handleRun = async () => {
    setClicking(true);
    setError(null);
    try {
      const response = await apiService.runHubSkillsSync();
      const data = response.data;
      if (data?.skipped) {
        setNotice(data.error || 'Hub skills sync is not configured');
        return;
      }
      if (data?.already_running) {
        setNotice(describeCurrentRun(data.current_run));
        setStatus((prev) =>
          prev
            ? {
                ...prev,
                sync_in_progress: true,
                current_run: data.current_run ?? prev.current_run,
              }
            : prev
        );
        return;
      }
      if (data?.started) {
        setNotice(null);
        setStatus((prev) =>
          prev
            ? { ...prev, sync_in_progress: true }
            : prev
        );
        return;
      }
      setError(data?.error || response.message || 'Hub skills sync was not started');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start Hub skills sync');
    } finally {
      setClicking(false);
    }
  };

  const running = Boolean(status?.sync_in_progress);
  const configured = status?.configured !== false;
  const disabled = loading || clicking || running || !configured || status == null;
  const lastOk = status?.last_successful_sync_at;
  const lastRun = status?.last_run_time;

  let pillLabel = 'Idle';
  let pillTone: 'idle' | 'run' | 'ok' | 'fail' | 'off' = 'idle';
  if (loading) {
    pillLabel = 'Checking…';
  } else if (!configured) {
    pillLabel = 'Not configured';
    pillTone = 'off';
  } else if (running) {
    pillLabel = 'Running…';
    pillTone = 'run';
  } else if (status?.last_result?.skipped) {
    pillLabel = 'Not configured';
    pillTone = 'off';
  } else if (status?.last_error && status.last_result?.success === false) {
    pillLabel = 'Last run failed';
    pillTone = 'fail';
  } else if (lastOk) {
    pillLabel = `Last synced ${formatRelative(lastOk)}`;
    pillTone = 'ok';
  } else {
    pillLabel = 'Not run yet';
  }

  const buttonTitle = running
    ? runningTooltip(status?.current_run)
    : !configured
      ? 'Hub skills sync is not configured on this environment'
      : 'Run Hub skills library score sync in the background';

  const modal = modalOpen && (
    <div
      className="hub-sync-modal-overlay"
      onClick={() => setModalOpen(false)}
      role="presentation"
    >
      <div
        className="hub-sync-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hub-sync-modal-title"
      >
        <div className="hub-sync-modal__header">
          <h2 id="hub-sync-modal-title" className="hub-sync-modal__title">
            Hub skills sync results
          </h2>
          <button
            type="button"
            className="hub-sync-modal__close"
            onClick={() => setModalOpen(false)}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="hub-sync-modal__body">
          <HubSyncResultsBody
            result={status?.last_result ?? null}
            finishedAt={status?.last_run_time}
          />
        </div>
        <div className="hub-sync-modal__footer">
          <button
            type="button"
            className="hub-sync-modal__done"
            onClick={() => setModalOpen(false)}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );

  const toolbarStats = variant === 'toolbar' && (
    <div className="hub-sync-control__stats" aria-label="Hub sync status">
      <div className="hub-sync-control__stat">
        <span className="hub-sync-control__stat-label">Status</span>
        <span className={`hub-sync-pill hub-sync-pill--${pillTone}`}>
          <span className="hub-sync-pill__dot" aria-hidden />
          <span>{pillLabel}</span>
        </span>
      </div>
      <div className="hub-sync-control__stat">
        <span className="hub-sync-control__stat-label">Last successful sync</span>
        <span className="hub-sync-control__stat-value" title={lastOk ? formatAbsolute(lastOk) : undefined}>
          {lastOk ? <time dateTime={lastOk}>{formatAbsolute(lastOk)}</time> : 'Never'}
        </span>
      </div>
      <div className="hub-sync-control__stat">
        <span className="hub-sync-control__stat-label">Last run</span>
        <span className="hub-sync-control__stat-value" title={lastRun ? formatAbsolute(lastRun) : undefined}>
          {lastRun ? <time dateTime={lastRun}>{formatAbsolute(lastRun)}</time> : '—'}
        </span>
      </div>
      <div className="hub-sync-control__stat">
        <span className="hub-sync-control__stat-label">Daily schedule</span>
        <span className="hub-sync-control__stat-value">
          {formatSchedule(status?.schedule)}
        </span>
      </div>
      <div className="hub-sync-control__stat">
        <span className="hub-sync-control__stat-label">Configuration</span>
        <span className="hub-sync-control__stat-value">
          {loading ? '…' : configured ? 'Ready' : 'Not configured'}
        </span>
      </div>
      {running && status?.current_run && (
        <div className="hub-sync-control__stat hub-sync-control__stat--wide">
          <span className="hub-sync-control__stat-label">Current run</span>
          <span className="hub-sync-control__stat-value" title={runningTooltip(status.current_run)}>
            {describeCurrentRun(status.current_run)}
          </span>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div
        className={`hub-sync-control${variant === 'toolbar' ? ' hub-sync-control--toolbar' : ''}`}
        role="region"
        aria-label="Hub skills sync"
      >
        {variant === 'toolbar' ? (
          <div className="hub-sync-control__toolbar">
            <div className="hub-sync-control__toolbar-action">
              <button
                type="button"
                className="hub-sync-control__btn"
                onClick={handleRun}
                disabled={disabled}
                title={buttonTitle}
              >
                {running || clicking ? 'Syncing…' : 'Run Hub Skills Sync'}
              </button>
            </div>
            {toolbarStats}
          </div>
        ) : (
          <>
            <div className="hub-sync-control__main">
              <div className="hub-sync-control__actions">
                <button
                  type="button"
                  className="hub-sync-control__btn"
                  onClick={handleRun}
                  disabled={disabled}
                  title={buttonTitle}
                >
                  {running || clicking ? 'Syncing…' : 'Run Hub Skills Sync'}
                </button>
                <span className={`hub-sync-pill hub-sync-pill--${pillTone}`}>
                  <span className="hub-sync-pill__dot" aria-hidden />
                  <span>{pillLabel}</span>
                </span>
              </div>
              <p className="hub-sync-control__last" title={lastOk ? formatAbsolute(lastOk) : undefined}>
                Last successful sync:{' '}
                {lastOk ? (
                  <time dateTime={lastOk}>{formatAbsolute(lastOk)}</time>
                ) : (
                  'Never synced yet'
                )}
              </p>
            </div>
            <Link to="/hub-sync-logs" className="hub-sync-control__logs">
              View sync logs →
            </Link>
          </>
        )}
      </div>
      {notice && (
        <p className="hub-sync-control__notice" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="hub-sync-control__error" role="alert">
          {error}
        </p>
      )}
      {typeof document !== 'undefined' && document.body && modal
        ? createPortal(modal, document.body)
        : null}
    </>
  );
};

export default HubSkillsSyncControl;
